import React, { useState, useCallback, useEffect } from 'react';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import IncomeCreator from './IncomeCreator';
import Overview from './Overview';
import Navigation from './Navigation';
import EventorSyncTab from './EventorSyncTab';
import SwishTab from './SwishTab';
import SettingsTab from './SettingsTab';
import ConfirmDialog from './ConfirmDialog';
import Button from '@mui/material/Button';
import { apiGetCompany, Role } from './Api';
import { Capability } from './generated/capabilities';
import { CapabilitiesProvider } from './Capabilities';
import { ScopeProvider } from './Scope';
import { ActiveRoleProvider } from './ActiveRole';
import { clearTargetDraft, consumeTargetCompany, consumeTargetDraft } from './deepLink';
import { useHashRoute, rememberedPath, RouteContext, RoutePrefixContext } from './route';


// CustomTabPanel.propTypes = {
//   children: PropTypes.node,
//   index: PropTypes.number.isRequired,
//   value: PropTypes.number.isRequired,
// };


export default function MainNavigation({onLogOut, roles, activeRoleId, userName, capabilities, scope, prepareAllScopes, onSwitchRole}: {
    onLogOut: () => void,
    roles: Role[],
    activeRoleId: number,
    capabilities: Capability[],
    scope: [string, string][],
    userName: string | null,
    prepareAllScopes: boolean,
    onSwitchRole: () => void,
}) {
    // The single source of the route, shared with the whole tree via RouteContext so each nested
    // sub-tab group can read/drive its own segment of the hash path.
    const route = useHashRoute()
    const { segments, navigate } = route
    const [version, setVersion] = useState(0)
    const [companyName, setCompanyName] = useState<string | null>(null)
    // A notification deep link (/?draft=<id>&company=<id>) opens the Attestera tab on that draft.
    // Read once.
    const [targetDraft] = useState(() => consumeTargetDraft())
    const [targetCompany] = useState(() => consumeTargetCompany())
    // The company the link was for, when it is not this one. Eko cannot switch: the company is
    // chosen in Visma's consent screen at login and a different one means a different token — so
    // this is said, not silently resolved.
    const [wrongCompany, setWrongCompany] = useState<string | null>(null)
    useEffect(() => {
        void apiGetCompany().then(company => {
            setCompanyName(company.name)
            if (targetDraft !== null && targetCompany !== null && targetCompany !== company.id) {
                setWrongCompany(targetCompany)
                clearTargetDraft()      // one shot, so a reload does not ask again
            }
        }).catch(() => {})
    }, [])
    const activeRole = roles.find(role => role.id === activeRoleId)
    // A tab click pushes its slug to the URL hash (so Back/Forward and reload track the active tab).
    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        // Back to where this tab was left, not to its first sub-tab.
        navigate(rememberedPath([tabs[newValue].slug]))
    };

    // A tab calls this when it has changed data the overview shows; bumping the
    // signal makes the (mounted, hidden) Overview refetch in the background, so it's
    // already fresh when the user switches to it.
    const handleUpdate = useCallback(
        () => { setVersion(v => v + 1) },
        []
    )

    // Tabs are driven by the active role's capabilities: a tab (and its panel) only
    // appears when the role can use it. The list order gives each visible tab a stable
    // index shared by its Tab and its panel, so hiding one never misaligns them.
    // `panel` is a render function taking whether this tab is currently active, so a panel can
    // kick off work the first time it's shown (e.g. the Eventor tab pre-loads its payment events)
    // without doing it on app load — every panel is mounted from the start, just hidden. A tab
    // shows if the role holds ANY of its capabilities (the Eventor tab groups three).
    const capabilitySet = new Set(capabilities)
    // `slug` is the stable, ASCII URL identity of a tab (a public contract once bookmarked/shared) —
    // independent of label wording and of which tabs a role can see.
    const allTabs: { slug: string, label: string, capabilities: Capability[], panel: (active: boolean) => React.ReactNode }[] = [
        { slug: 'oversikt', label: 'Översikt', capabilities: ['overview.view'], panel: () => <Overview refreshSignal={version}/> },
        { slug: 'fakturera', label: 'Fakturera', capabilities: ['customer_invoice.send'], panel: () => <IncomeCreator onUpdate={handleUpdate}/> },
        { slug: 'attestera', label: 'Attestera', capabilities: ['expense.attest'], panel: () => <Navigation onUpdate={handleUpdate} targetDraftId={targetDraft}/> },
        { slug: 'synk', label: 'Synkronisera med Eventor',
          capabilities: ['eventor.import_invoices', 'eventor.register_payments', 'member.import'],
          panel: active => <EventorSyncTab active={active} onUpdate={handleUpdate}/> },
        { slug: 'swish', label: 'Swish och Zettle', capabilities: ['swish.book'], panel: active => <SwishTab active={active}/> },
        { slug: 'installningar', label: 'Inställningar', capabilities: ['admin.roles', 'admin.company_config', 'config.budget'], panel: () => <SettingsTab/> },
    ]
    const tabs = allTabs.filter(tab => tab.capabilities.some(c => capabilitySet.has(c)))
    // The visible tab is whichever the hash's first slug names; an unknown/empty hash falls back to
    // the first tab (and is normalised below).
    const foundIndex = tabs.findIndex(tab => tab.slug === segments[0])
    const activeIndex = foundIndex >= 0 ? foundIndex : 0

    // Mount a tab's panel only once it's first opened, then keep it mounted (hidden). So on load only
    // the visible tab fetches — it never waits behind hidden tabs' data — and a tab, once visited,
    // stays warm (no refetch on return). Tracked by label so capability-driven reordering can't
    // misfire it.
    const [activated, setActivated] = useState<Set<string>>(() => {
        const label = tabs[activeIndex]?.label
        return label ? new Set([label]) : new Set()
    })
    useEffect(() => {
        const label = tabs[activeIndex]?.label
        if (label !== undefined) setActivated(s => s.has(label) ? s : new Set(s).add(label))
    }, [activeIndex])  // tabs is stable within a role; MainNavigation remounts on role switch

    // On load, resolve the URL to a valid tab (replace, so the correction leaves no history entry):
    // a notification deep link (/?draft=<id>) wins and opens Attestera; otherwise an empty/unknown
    // hash is normalised to the current (default) tab so the address bar always shows a real slug.
    useEffect(() => {
        if (tabs.length === 0) return
        // The company is checked asynchronously, so the tab may open before the mismatch is
        // known. That is the right way round: with the stash cleared, Attestera shows its ordinary
        // list and the dialog says why the draft is not in it.
        if (targetDraft && tabs.some(tab => tab.slug === 'attestera')) { navigate(['attestera'], { replace: true }); return }
        if (foundIndex < 0) navigate([tabs[activeIndex].slug], { replace: true })
    }, [])  // once on mount

    return (
        <RouteContext.Provider value={route}>
        <CapabilitiesProvider capabilities={capabilities}>
        <ScopeProvider scope={scope} initialAllScopes={prepareAllScopes}>
        <ActiveRoleProvider name={activeRole?.name ?? null}>
        <Box sx={{ width: '100%' }}>
        {/* A link from another company's digest. Logging out is the only route to it, since the
            company comes from Visma's consent screen — so that is what the dialog offers. */}
        <ConfirmDialog
            open={wrongCompany !== null}
            title="Underlaget hör till en annan förening"
            confirmLabel="Logga ut"
            cancelLabel="Stanna kvar"
            message={<>
                <p>
                    Länken gäller <strong>{wrongCompany}</strong>, men du är inloggad
                    i <strong>{companyName ?? '—'}</strong>.
                </p>
                <p>
                    Logga ut och välj {wrongCompany} i Visma för att öppna det. Klicka sedan på
                    länken i mejlet igen.
                </p>
            </>}
            onCancel={() => setWrongCompany(null)}
            onConfirm={onLogOut}/>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ pl: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <span style={{ fontWeight: 'bold' }}>{companyName ?? ''}</span>
                {/* Who, then as what. The role alone was ambiguous on a shared computer, which is
                    exactly where acting as the wrong person matters. */}
                {userName !== null && <span>· {userName}</span>}
                {activeRole && <span>· {activeRole.name}</span>}
                {roles.length > 1 && <Button size="small" onClick={onSwitchRole}>Byt roll</Button>}
            </Box>
            <Button variant="text" onClick={onLogOut}>Logga ut</Button>
        </Box>
        {tabs.length === 0 ? (
            <Box sx={{ p: 2 }}>
                <p>Den här rollen har inga funktioner aktiverade än.</p>
                <p>Kontakta en administratör.</p>
            </Box>
        ) : (
            <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={activeIndex} onChange={handleChange}>
                {tabs.map(tab => <Tab key={tab.label} label={tab.label}/>)}
                </Tabs>
            </Box>
            {tabs.map((tab, index) => <div key={tab.label} hidden={activeIndex !== index}>
                {/* Each top panel roots its subtree at its own slug (a stable primitive, so this
                    context doesn't churn), so a nested sub-tab group reads the segment past it. */}
                {activated.has(tab.label)
                    ? <RoutePrefixContext.Provider value={tab.slug}>{tab.panel(activeIndex === index)}</RoutePrefixContext.Provider>
                    : null}
            </div>)}
            </>
        )}
        </Box>
        </ActiveRoleProvider>
        </ScopeProvider>
        </CapabilitiesProvider>
        </RouteContext.Provider>
    );
}
